<template>
    <CardDropdown :item="item">
        <template #icon>
            <AvatarSm :image="testimonial.photo" :icon="avatarIcon" />
        </template>
        <template #extra>
            <InvisibleIcon
                v-if="testimonial.hide"
                @click.stop="toggleHide"
                class="flex-shrink-0 w-4 h-4 ct-fg-4 hover:ct-fg-2"
            />
            <BadgeWarningMd
                v-tooltip="$t('love.reviews.accept_info')"
                v-if="!testimonial.isPublished"
                class="flex-shrink-0 flex items-center space-x-1"
            >
                <span>{{ $t("love.reviews_list.new") }}</span>
            </BadgeWarningMd>
        </template>
    </CardDropdown>
</template>

<script>
import { markRaw } from "vue";
import { mapActions, mapState } from "vuex";
import { CardDropdown } from "@easy/ui/cards";
import { BadgeWarningMd } from "@easy/ui/badges";
import {
    InvisibleIcon,
    CheckIcon,
    ImageIcon,
    MailIcon,
    PencilSquareIcon,
    CopyDocumentIcon,
    TrashBinIcon,
    VisibleIcon,
    VerifiedTickIcon,
    VerifiedGoogleIcon,
} from "@easy/ui/icons";
import { AvatarSm } from "@easy/ui/avatars";

export default {
    components: {
        CardDropdown,
        BadgeWarningMd,
        InvisibleIcon,
        AvatarSm,
    },
    props: {
        testimonial: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ["edit", "confirm", "generate-image", "ask-for-confirmation"],
    computed: {
        ...mapState("love", ["wall"]),

        avatarIcon() {
            return this.testimonial.source === "google"
                ? markRaw(VerifiedGoogleIcon)
                : this.testimonial.confirmed
                  ? markRaw(VerifiedTickIcon)
                  : null;
        },
        item() {
            const actions = [
                {
                    label: this.$t("shared.edit"),
                    icon: markRaw(PencilSquareIcon),
                    handler: () => {
                        this.$emit("edit", this.testimonial);
                    },
                },
                {
                    label: this.$t("shared.duplicate"),
                    icon: markRaw(CopyDocumentIcon),
                    handler: this.duplicate,
                },
                {
                    label: this.$t("shared.generate_image"),
                    icon: markRaw(ImageIcon),
                    handler: () => {
                        this.$emit("generate-image");
                    },
                },
                {
                    label: this.testimonial.hide ? this.$t("shared.show") : this.$t("shared.hide"),
                    icon: this.testimonial.hide ? markRaw(VisibleIcon) : markRaw(InvisibleIcon),
                    handler: this.toggleHide,
                },
                {
                    label: this.$t("shared.ask_for_confirmation"),
                    icon: markRaw(MailIcon),
                    handler: () => {
                        this.$emit("ask-for-confirmation");
                    },
                },
                { isDivider: true },
                {
                    label: this.$t("shared.delete"),
                    icon: markRaw(TrashBinIcon),
                    isDanger: true,
                    handler: this.remove,
                },
            ];

            if (!this.testimonial.isPublished) {
                actions.unshift({ isDivider: true });
                actions.unshift({
                    label: this.$t("love.reviews.accept"),
                    icon: markRaw(CheckIcon),
                    handler: this.publish,
                });
            }

            return {
                title: this.testimonial.name,
                subtitle: `${this.testimonial.rate} ${this.$t("love.stars", +this.testimonial.rate)} `,
                actions,
            };
        },
    },
    methods: {
        ...mapActions("love", ["removeTestimonial", "cloneTestimonial", "publishTestimonial"]),
        ...mapActions("modals", ["showConfirmModal"]),
        toggleHide() {
            this.testimonial.hide = !this.testimonial.hide;
            if (!this.testimonial._created) {
                this.testimonial._changed = true;
            }
        },
        duplicate() {
            this.cloneTestimonial(this.testimonial);
        },
        publish() {
            this.publishTestimonial(this.testimonial);
        },
        async remove() {
            const { $t } = this;
            const action = await this.showConfirmModal({
                title: $t("love.reviews.remove_title"),
                description: $t("love.reviews.remove_message"),
                ctaLabel: $t("shared.delete"),
                cancelLabel: $t("shared.cancel"),
            });
            if (action) {
                this.removeTestimonial(this.testimonial);
            }
        },
    },
};
</script>
